import { ArrowUpRight, EnvelopeSimple, Phone } from '@phosphor-icons/react';
import type {
  AboutValues,
  ContactValues,
  EducationValues,
  ExperienceValues,
  HeroValues,
  ProjectsValues,
} from '../../schemas';
import { PLATFORM_META } from '../../linkPlatforms';
import type { TemplateProps } from './shared';

const BG = '#09090B';
const PANEL = '#121217';
const BORDER = '#1E1E26';
const MUTED = '#9CA3AF';
const FAINT = '#6B7280';
const BAT = '#F5C518';

export function BatmanTemplate({ draft, enabled, ownerName }: TemplateProps) {
  return (
    <div
      className="min-h-full font-sans"
      style={{ background: BG, color: '#F4F4F5' }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[320px]"
        style={{
          background: `radial-gradient(ellipse 60% 80% at 50% 0%, ${BAT}22 0%, transparent 70%)`,
        }}
      />

      <div className="relative mx-auto max-w-4xl px-8 py-12">
        <header
          className="flex items-center justify-between border-b pb-5 text-[11px] uppercase tracking-[0.25em]"
          style={{ borderColor: BORDER, color: FAINT }}
        >
          <div className="flex items-center gap-2.5">
            <BatSigil />
            <span className="font-semibold" style={{ color: BAT }}>
              {ownerName}
            </span>
          </div>
          <span>Gotham · {new Date().getFullYear()}</span>
        </header>

        {enabled.has('hero') && draft.hero ? <HeroBlock values={draft.hero} /> : null}
        {enabled.has('about') && draft.about ? <AboutBlock values={draft.about} /> : null}
        {enabled.has('projects') && draft.projects ? (
          <ProjectsBlock values={draft.projects} />
        ) : null}
        {enabled.has('experience') && draft.experience ? (
          <ExperienceBlock values={draft.experience} />
        ) : null}
        {enabled.has('education') && draft.education ? (
          <EducationBlock values={draft.education} />
        ) : null}
        {enabled.has('contact') && draft.contact ? (
          <ContactBlock values={draft.contact} />
        ) : null}

        <footer
          className="mt-20 border-t pt-5 text-center text-[11px] uppercase tracking-[0.25em]"
          style={{ borderColor: BORDER, color: FAINT }}
        >
          <span style={{ color: BAT }}>◂</span> I am vengeance. I am the night.{' '}
          <span style={{ color: BAT }}>▸</span>
        </footer>
      </div>
    </div>
  );
}

function BatSigil() {
  return (
    <svg width="18" height="11" viewBox="0 0 24 14" fill={BAT} aria-hidden>
      <path d="M12 0c-.4 2 .3 3 1.8 3.5-.5-.6-.4-1.3 0-1.6.3 1 1.2 1.7 2.3 2 .8-1.1 2.3-1.8 3.6-1.8.7 0 1.7.2 2.6.5-.6.8-1.4 1.6-2.1 2 1.4.4 2.5 1.1 3.5 2-2.5.3-4.5 1.2-5.9 2.5-1.5 1.4-2.4 3-4.6 2.7-.3 0-.7-.1-1.2-.3-.5.2-.9.3-1.2.3-2.2.3-3.1-1.3-4.6-2.7C4.8 7.8 2.8 6.9.3 6.6c1-.9 2.1-1.6 3.5-2C3 4.1 2.2 3.3 1.6 2.5 2.5 2.2 3.5 2 4.2 2c1.3 0 2.8.7 3.6 1.8 1.1-.3 2-1 2.3-2 .4.3.5 1-.1 1.6C11.7 3 12.4 2 12 0z" />
    </svg>
  );
}

function SectionHeading({ num, title }: { num: string; title: string }) {
  return (
    <div
      className="mt-16 mb-7 flex items-baseline gap-3 border-b pb-3"
      style={{ borderColor: BORDER }}
    >
      <span className="font-mono text-[11px] tracking-[0.25em]" style={{ color: BAT }}>
        {num}
      </span>
      <h2 className="font-display text-2xl font-bold tracking-tight">{title}</h2>
    </div>
  );
}

function HeroBlock({ values }: { values: HeroValues }) {
  const availabilityLabel: Record<HeroValues['availability'], string> = {
    available: 'The signal is lit',
    limited: 'Selective contracts',
    closed: 'Off the grid',
  };
  return (
    <section className="pt-14">
      {values.tagline ? (
        <p
          className="font-mono text-[11px] uppercase tracking-[0.3em]"
          style={{ color: BAT }}
        >
          ▸ {values.tagline}
        </p>
      ) : null}
      <h1 className="mt-4 font-display text-5xl font-black leading-[1.02] tracking-tight">
        {values.headline}
      </h1>
      {values.subheadline ? (
        <p
          className="mt-5 max-w-[60ch] text-lg leading-relaxed"
          style={{ color: MUTED }}
        >
          {values.subheadline}
        </p>
      ) : null}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        {values.ctaLabel && values.ctaHref ? (
          <a
            href={values.ctaHref}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-bold transition-transform hover:-translate-y-0.5"
            style={{ background: BAT, color: '#09090B' }}
          >
            {values.ctaLabel}
            <ArrowUpRight size={12} weight="bold" />
          </a>
        ) : null}
        <span
          className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium"
          style={{
            borderColor: values.availability === 'available' ? BAT : BORDER,
            color: values.availability === 'available' ? BAT : MUTED,
          }}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${values.availability === 'available' ? 'animate-pulse' : ''}`}
            style={{
              background: values.availability === 'available' ? BAT : MUTED,
            }}
          />
          {availabilityLabel[values.availability]}
        </span>
      </div>
    </section>
  );
}

function AboutBlock({ values }: { values: AboutValues }) {
  return (
    <section>
      <SectionHeading num="01" title="Dossier" />
      <p className="max-w-[62ch] whitespace-pre-line text-base leading-relaxed" style={{ color: MUTED }}>
        {values.body}
      </p>
      {values.skills.length ? (
        <div className="mt-6">
          <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: FAINT }}>
            Arsenal
          </p>
          <div className="flex flex-wrap gap-1.5">
            {values.skills.map((s) => (
              <span
                key={s}
                className="rounded-md border px-2 py-1 text-xs font-medium"
                style={{ borderColor: BORDER, background: PANEL, color: '#F4F4F5' }}
              >
                {s}
              </span>
            ))}
          </div>
        </div>
      ) : null}
      {values.resumeUrl ? (
        <a
          href={values.resumeUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold"
          style={{ color: BAT }}
        >
          Request classified file <ArrowUpRight size={12} weight="bold" />
        </a>
      ) : null}
    </section>
  );
}

function ProjectsBlock({ values }: { values: ProjectsValues }) {
  if (!values.items.length) return null;
  return (
    <section>
      <SectionHeading num="02" title="Case files" />
      <div className="space-y-6">
        {values.items.map((p, i) => (
          <article
            key={p.id}
            className="group relative flex flex-col overflow-hidden rounded-lg border p-6 transition-colors"
            style={{ borderColor: BORDER, background: PANEL }}
          >
            <div
              className="absolute left-0 top-0 h-full w-[3px] transition-colors group-hover:opacity-100"
              style={{ background: BAT, opacity: 0.6 }}
            />
            <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.25em]" style={{ color: FAINT }}>
              <span>File №{String(i + 1).padStart(2, '0')}</span>
              <span>{p.year}</span>
            </div>
            {p.mediaKind === 'image' && p.mediaUrl ? (
              <div
                className="-mx-6 my-4 overflow-hidden border-y"
                style={{ borderColor: BORDER }}
              >
                <img
                  src={p.mediaUrl}
                  alt={p.mediaAlt ?? p.title}
                  className="aspect-[16/10] w-full object-cover grayscale transition-all duration-500 group-hover:grayscale-0"
                />
              </div>
            ) : p.mediaKind === 'video' && p.mediaUrl ? (
              <div
                className="-mx-6 my-4 grid aspect-[16/10] w-[calc(100%+3rem)] place-items-center border-y text-[11px]"
                style={{ borderColor: BORDER, background: BG, color: FAINT }}
              >
                ▶ surveillance feed
              </div>
            ) : null}
            <h3 className="mt-2 font-display text-xl font-bold leading-tight">{p.title}</h3>
            {(p.role || p.client) && (
              <p className="mt-1 text-[11px] uppercase tracking-[0.2em]" style={{ color: BAT }}>
                {[p.role, p.client].filter(Boolean).join(' · ')}
              </p>
            )}
            {p.summary ? (
              <p className="mt-3 text-sm leading-relaxed" style={{ color: MUTED }}>
                {p.summary}
              </p>
            ) : null}
            {p.bullets && p.bullets.length ? (
              <ul className="mt-3 flex-1 space-y-1.5 text-sm" style={{ color: MUTED }}>
                {p.bullets.map((b, i) => (
                  <li key={i} className="flex gap-2">
                    <span style={{ color: BAT }}>▸</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            {p.tags && p.tags.length ? (
              <div className="mt-3 flex flex-wrap gap-1">
                {p.tags.map((t) => (
                  <span
                    key={t}
                    className="rounded px-1.5 py-0.5 text-[10px]"
                    style={{ background: BG, color: MUTED }}
                  >
                    {t}
                  </span>
                ))}
              </div>
            ) : null}
            {p.url ? (
              <a
                href={p.url}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-1 text-sm font-semibold"
                style={{ color: BAT }}
              >
                Open file <ArrowUpRight size={12} weight="bold" />
              </a>
            ) : null}
          </article>
        ))}
      </div>
    </section>
  );
}

function ExperienceBlock({ values }: { values: ExperienceValues }) {
  if (!values.items.length) return null;
  return (
    <section>
      <SectionHeading num="03" title="Record" />
      <div className="space-y-5">
        {values.items.map((e) => (
          <div
            key={e.id}
            className="rounded-lg border px-5 py-4"
            style={{ borderColor: BORDER, background: PANEL }}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="font-display text-lg font-bold tracking-tight">{e.role}</h3>
              <span className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: BAT }}>
                {e.start} — {e.current ? 'Ongoing' : e.end}
              </span>
            </div>
            <p className="mt-1 text-sm" style={{ color: MUTED }}>
              {e.company}
              {e.location ? ` · ${e.location}` : ''}
            </p>
            {e.summary ? (
              <p className="mt-3 max-w-[62ch] text-sm leading-relaxed" style={{ color: '#E5E7EB' }}>
                {e.summary}
              </p>
            ) : null}
            {e.highlights && e.highlights.length ? (
              <ul className="mt-3 space-y-1.5 text-sm" style={{ color: MUTED }}>
                {e.highlights.map((h, i) => (
                  <li key={i} className="flex gap-2">
                    <span style={{ color: BAT }}>▸</span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
}

function EducationBlock({ values }: { values: EducationValues }) {
  if (!values.items.length) return null;
  return (
    <section>
      <SectionHeading num="04" title="Training" />
      <div className="space-y-4">
        {values.items.map((e) => (
          <div
            key={e.id}
            className="rounded-lg border px-5 py-4"
            style={{ borderColor: BORDER, background: PANEL }}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="font-display text-lg font-bold tracking-tight">{e.institution}</h3>
              <span className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: BAT }}>
                {e.startYear}
                {e.endYear ? ` – ${e.endYear}` : ''}
              </span>
            </div>
            <p className="mt-1 text-sm" style={{ color: MUTED }}>
              {e.credential}
              {e.gpa ? ` · GPA ${e.gpa}` : ''}
            </p>
            {e.note ? (
              <p className="mt-2 max-w-[60ch] text-sm" style={{ color: MUTED }}>
                {e.note}
              </p>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
}

function ContactBlock({ values }: { values: ContactValues }) {
  return (
    <section>
      <SectionHeading num="05" title="Bat-signal" />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {values.email ? (
          <a
            href={`mailto:${values.email}`}
            className="flex items-center justify-between rounded-lg border px-4 py-3 text-sm font-medium transition-colors hover:border-yellow-400"
            style={{ borderColor: BORDER, background: PANEL }}
          >
            <span className="inline-flex items-center gap-2">
              <EnvelopeSimple size={14} style={{ color: BAT }} />
              {values.email}
            </span>
            <ArrowUpRight size={12} weight="bold" style={{ color: FAINT }} />
          </a>
        ) : null}
        {values.phone ? (
          <a
            href={`tel:${values.phone.replace(/\s+/g, '')}`}
            className="flex items-center justify-between rounded-lg border px-4 py-3 text-sm font-medium transition-colors hover:border-yellow-400"
            style={{ borderColor: BORDER, background: PANEL }}
          >
            <span className="inline-flex items-center gap-2">
              <Phone size={14} style={{ color: BAT }} />
              {values.phone}
            </span>
            <ArrowUpRight size={12} weight="bold" style={{ color: FAINT }} />
          </a>
        ) : null}
        {values.links.map((l) => {
          const meta = PLATFORM_META[l.platform] ?? PLATFORM_META.custom;
          const Icon = meta.icon;
          const href =
            l.platform === 'email' && !l.href.startsWith('mailto:') ? `mailto:${l.href}` : l.href;
          return (
            <a
              key={l.id}
              href={href}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-between rounded-lg border px-4 py-3 text-sm font-medium"
              style={{ borderColor: BORDER, background: PANEL }}
            >
              <span className="inline-flex items-center gap-2">
                <Icon size={14} weight="regular" style={{ color: BAT }} />
                {l.label || meta.label}
              </span>
              <ArrowUpRight size={12} weight="bold" style={{ color: FAINT }} />
            </a>
          );
        })}
      </div>
      {values.location ? (
        <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: FAINT }}>
          Coordinates · {values.location}
        </p>
      ) : null}
      {values.allowInquiryForm ? (
        <div
          className="mt-6 rounded-lg border border-dashed p-5"
          style={{ borderColor: BAT, background: PANEL }}
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: BAT }}>
            ▸ Encrypted channel
          </p>
          <p className="mt-2 text-sm" style={{ color: MUTED }}>
            A secure inquiry form renders here on your live site.
          </p>
        </div>
      ) : null}
    </section>
  );
}
